import React from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForClasses } from "../../../utils/confighelpers";
import { FieldError, FieldLabel, inputClass } from "./ui";

/**
 * Coach / instructor of a session (2026-10-25).
 *  - The class has a default coach (classes.instructorName, set in My Classes).
 *  - "Add sessions" shows it pre-filled and lets the club pick another coach; it applies only to the sessions created.
 *  - A substitute is set per session ("Change coach" in the session panel), that session only.
 * Suggestions: GET {env}/schools/coachNames (names the club used before + the owner), loaded once per page visit.
 */

let namesCache = null; // { promise, names }

export function useCoachNames(open = true) {
  const [names, setNames] = React.useState(namesCache?.names || []);
  React.useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    if (!namesCache) {
      namesCache = {
        names: [],
        promise: axiosInstance({ method: "get", url: `${getUrlForClasses()}coachNames` })
          .then((res) => (res?.data?.statusCode === 200 && Array.isArray(res.data.data?.names) ? res.data.data.names : []))
          .catch(() => []),
      };
      namesCache.promise.then((list) => {
        if (namesCache) namesCache.names = list;
      });
    }
    namesCache.promise.then((list) => alive && setNames(list));
    return () => {
      alive = false;
    };
  }, [open]);
  return names;
}

/** After saving a new name, make it a suggestion straight away (and refresh from the server next time). */
export const rememberCoachName = (name) => {
  const n = String(name || "").trim();
  if (!n) return;
  if (namesCache && !namesCache.names.some((x) => x.toLowerCase() === n.toLowerCase())) {
    namesCache.names = [n, ...namesCache.names];
    namesCache.promise = Promise.resolve(namesCache.names);
  }
};

export const cleanCoachName = (v) => String(v || "").replace(/\s+/g, " ").trim().slice(0, 80);

/** Text field + up to 6 suggestion chips (filtered by what's typed). */
export function CoachField({ id, label = "Coach", value, onChange, names = [], hint = "", error = "", placeholder = "e.g. Lotte Jansen" }) {
  const q = cleanCoachName(value).toLowerCase();
  // A complete known name in the field (e.g. the pre-filled default) shows the other coaches; while typing
  // something new, only the names that match.
  const known = names.some((n) => n.toLowerCase() === q);
  const chips = names.filter((n) => n.toLowerCase() !== q && (!q || known || n.toLowerCase().includes(q))).slice(0, 6);
  return (
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <input
        id={id}
        type="text"
        value={value}
        maxLength={80}
        autoComplete="off"
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass(error)}
      />
      {chips.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" aria-label="Coaches you used before">
          {chips.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              className="flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-[13px] font-bold text-gray-700 hover:border-gray-300 hover:bg-gray-50"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5 text-gray-400" aria-hidden="true">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21a8 8 0 0 1 16 0" />
              </svg>
              {n}
            </button>
          ))}
        </div>
      )}
      {hint && !error && <p className="mt-1.5 text-xs text-gray-500">{hint}</p>}
      <FieldError>{error}</FieldError>
    </div>
  );
}
